/**
 * tienda.service.ts — Llamadas API para la Tienda Virtual.
 *
 * Exporta: tiendaService
 *
 * Depende de:
 *   - api (services/api.ts) para llamadas autenticadas (admin)
 *   - axios directo para llamadas públicas (no requieren auth)
 *
 * NO debe contener:
 *   - Lógica React (hooks, JSX)
 *   - Manipulación directa de estado
 */
import api from "@/services/api";
import axios from "axios";
import {
  unwrapApiData,
  unwrapApiEntity,
  unwrapApiList,
} from "@/lib/unwrapApiResponse";
import type {
  TiendaConfig,
  DatosPedido,
  PedidoItem,
  Pedido,
  EstadoPedido,
  DevolucionPedido,
  FranjaEntregaPedido,
  MetodoPagoTienda,
  MesaPublica,
  CanjeCarrito,
} from "../types/tienda.types";
import type { Producto } from "@/types";
import { mesasService } from "@/features/mesas/services/mesas.service";
import { DEFAULT_TIENDA_CONFIG } from "../constants/defaultTiendaConfig";
import {
  shouldPreferLocalDemoOps,
  shouldFallbackToLocalStorage,
} from "@/lib/localDemoOps";
import { txRead, txWrite } from "@/lib/persistence/transactionalStore";

const PUBLIC_BASE = "tienda";

/** Máximo de perfiles de datos que el cliente puede guardar por negocio. */
export const MAX_CHECKOUT_PROFILES = 3;

/** Perfil reutilizable de datos del cliente (nombre, teléfono y dirección). */
export interface TiendaCheckoutProfile {
  id: string;
  /** Nombre corto elegido por el cliente: "Casa", "Oficina", "Mamá"… */
  etiqueta: string;
  nombre: string;
  telefono: string;
  direccion: string;
  actualizadoEn: string;
}

/** Entrada para crear/actualizar un perfil desde la UI. */
export type TiendaCheckoutProfileInput = Omit<
  TiendaCheckoutProfile,
  "id" | "actualizadoEn"
> & { id?: string };

/** Datos locales de comodidad, separados de los pedidos y de la configuración del negocio. */
export interface TiendaCheckoutPreferences {
  nombre: string;
  telefono: string;
  direccion: string;
  tipoEntrega: "domicilio" | "local";
  /** Perfiles guardados explícitamente por el cliente (máx. MAX_CHECKOUT_PROFILES). */
  perfiles: TiendaCheckoutProfile[];
  /** Perfil cargado actualmente; permite resaltarlo y actualizarlo. */
  perfilActivoId: string | null;
}

const CHECKOUT_PREFERENCES_DEFAULT: TiendaCheckoutPreferences = {
  nombre: "",
  telefono: "",
  direccion: "",
  tipoEntrega: "domicilio",
  perfiles: [],
  perfilActivoId: null,
};

const checkoutPreferencesKey = (businessId: string): string =>
  `tienda_checkout_preferences:${businessId}`;

/** Id efímero y local para un perfil; no requiere backend. */
const createCheckoutProfileId = (): string =>
  `perfil_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;

/**
 * Normaliza lo leído del almacenamiento:
 * - Sanea perfiles y recorta a MAX_CHECKOUT_PROFILES.
 * - Migra el formato legacy (objeto plano sin `perfiles`) a un primer perfil
 *   solo si el registro no trae ya la clave `perfiles`, para no resucitar
 *   perfiles borrados.
 */
const normalizeCheckoutPreferences = (
  raw:
    | (Partial<TiendaCheckoutPreferences> & {
        perfiles?: TiendaCheckoutProfile[];
      })
    | null
    | undefined,
): TiendaCheckoutPreferences => {
  const base = { ...CHECKOUT_PREFERENCES_DEFAULT, ...(raw ?? {}) };
  const hasPerfilesKey = Array.isArray(raw?.perfiles);
  const perfiles: TiendaCheckoutProfile[] = hasPerfilesKey
    ? (raw?.perfiles ?? [])
        .filter((p): p is TiendaCheckoutProfile =>
          Boolean(p && typeof p === "object"),
        )
        .slice(0, MAX_CHECKOUT_PROFILES)
        .map((p) => ({
          id: p.id || createCheckoutProfileId(),
          etiqueta: (p.etiqueta || "").trim(),
          nombre: p.nombre || "",
          telefono: p.telefono || "",
          direccion: p.direccion || "",
          actualizadoEn: p.actualizadoEn || new Date().toISOString(),
        }))
    : [];

  if (!hasPerfilesKey && (base.nombre.trim() || base.telefono.trim())) {
    perfiles.push({
      id: createCheckoutProfileId(),
      etiqueta: "Mis datos",
      nombre: base.nombre,
      telefono: base.telefono,
      direccion: base.direccion,
      actualizadoEn: new Date().toISOString(),
    });
  }

  const perfilActivoId =
    base.perfilActivoId && perfiles.some((p) => p.id === base.perfilActivoId)
      ? base.perfilActivoId
      : null;

  return { ...base, perfiles, perfilActivoId };
};

/** Cliente sin auth para endpoints públicos */
const publicApi = axios.create({
  baseURL: import.meta.env.DEV
    ? "/api"
    : import.meta.env.VITE_API_URL || "/api",
  timeout: 10000,
  headers: { "Content-Type": "application/json" },
});

export const tiendaService = {
  // ── Públicos (no requieren autenticación) ────────────────────────────────

  async getConfig(slug: string): Promise<TiendaConfig> {
    try {
      // api base es /api, por lo tanto no se debe repetir /api en el path
      const { data } = await api.get(`tienda/${slug}/config`);
      return unwrapApiData<TiendaConfig>(data, DEFAULT_TIENDA_CONFIG);
    } catch {
      return DEFAULT_TIENDA_CONFIG;
    }
  },

  async getProductos(slug: string): Promise<Producto[]> {
    const filtrarPublicables = (all: Producto[]): Producto[] =>
      all.filter(
        (p) =>
          p.isActive !== false &&
          p.isForSale === true &&
          p.soloInventario !== true &&
          p.estado !== "descontinuado",
      );
    // En modo demo/local leer directamente del localStorage para evitar que
    // una respuesta vacía del servidor sobreescriba el catálogo real. Si el
    // local está vacío (demo sin sembrar o usuario sin productos), cae al API
    // para no dejar selectores de administración sin catálogo.
    if (shouldPreferLocalDemoOps()) {
      try {
        const locales = filtrarPublicables(
          txRead("inventory_products", [] as Producto[]),
        );
        if (locales.length > 0) return locales;
      } catch {
        /* cae al API */
      }
    }
    try {
      const { data } = await api.get(`tienda/${slug}/productos`);
      const list = unwrapApiList<Producto>(data, "productos");
      return list;
    } catch {
      // Fallback: leer desde localStorage como respaldo
      try {
        return filtrarPublicables(
          txRead("inventory_products", [] as Producto[]),
        );
      } catch {
        return [];
      }
    }
  },

  async crearPedido(
    slug: string,
    items: PedidoItem[],
    cliente: DatosPedido,
    canal: "web" | "whatsapp" = "web",
    tipoEntrega: "domicilio" | "local" = "domicilio",
    detallesEntrega?: {
      franjaEntrega?: FranjaEntregaPedido;
      mensajeFinalSnapshot?: string;
      /** Costo de envío cobrado (0 = gratis o sin envío). */
      costoEnvio?: number;
      /** Mesa elegida (solo `tipoEntrega === "local"`). */
      mesaId?: string;
      mesaNumero?: string;
      mesaZona?: string;
      /** Premios/beneficios canjeados con puntos. */
      canjes?: CanjeCarrito[];
      /** Descuento total (COP) aplicado por canjes. */
      descuentoCanjes?: number;
    },
  ): Promise<Pedido> {
    const { data } = await publicApi.post(`tienda/${slug}/pedido`, {
      items,
      cliente,
      canal,
      tipoEntrega,
      ...detallesEntrega,
    });
    return unwrapApiEntity<Pedido>(data, {} as Pedido);
  },

  /**
   * Mesas activas del negocio para el selector del checkout público. Intenta el
   * endpoint público; si no está disponible (demo/red) cae al contrato público
   * del módulo Mesas.
   */
  async getMesas(slug: string): Promise<MesaPublica[]> {
    const normalizar = (
      mesas: Array<{
        id: string;
        numero: string;
        zona?: string;
        activa?: boolean;
      }>,
    ): MesaPublica[] =>
      mesas
        .filter((m) => m.activa !== false)
        .map((m) => ({ id: m.id, numero: m.numero, zona: m.zona }));

    try {
      const { data } = await publicApi.get(`tienda/${slug}/mesas`);
      const list = unwrapApiList<MesaPublica>(data, "mesas");
      if (list.length > 0) return list;
    } catch {
      /* cae al fallback */
    }
    try {
      return normalizar(await mesasService.getMesas());
    } catch {
      return [];
    }
  },

  /**
   * Busca un pedido por id desde la tienda pública (sin auth).
   * En demo/local lee de `tienda_pedidos`; en modo remoto intenta el endpoint
   * público y cae al almacén local si la red/API no está disponible.
   * Devuelve null si no existe (pedido de otra tienda o enlace caducado).
   */
  async getPedidoById(slug: string, pedidoId: string): Promise<Pedido | null> {
    const buscarLocal = (): Pedido | null => {
      const raw = txRead("tienda_pedidos", []);
      const pedidos = Array.isArray(raw) ? (raw as Pedido[]) : [];
      return pedidos.find((p) => p.id === pedidoId) ?? null;
    };
    if (shouldPreferLocalDemoOps()) return buscarLocal();
    try {
      const { data } = await publicApi.get(`tienda/${slug}/pedido/${pedidoId}`);
      return unwrapApiEntity<Pedido>(data, null as unknown as Pedido);
    } catch (err) {
      if (shouldFallbackToLocalStorage(err)) return buscarLocal();
      throw err;
    }
  },

  getCheckoutPreferences(businessId: string): TiendaCheckoutPreferences {
    const stored = txRead(
      checkoutPreferencesKey(businessId),
      CHECKOUT_PREFERENCES_DEFAULT,
    );
    return normalizeCheckoutPreferences(stored);
  },

  saveCheckoutPreferences(
    businessId: string,
    updates: Partial<TiendaCheckoutPreferences>,
  ): TiendaCheckoutPreferences {
    const next = normalizeCheckoutPreferences({
      ...this.getCheckoutPreferences(businessId),
      ...updates,
    });
    txWrite(checkoutPreferencesKey(businessId), next);
    return next;
  },

  /**
   * Crea o actualiza un perfil guardado.
   * - Con `id` de un perfil existente: lo sobrescribe y lo deja activo.
   * - Sin `id`: agrega uno nuevo. Si ya hay MAX_CHECKOUT_PROFILES, no hace nada
   *   y devuelve las preferencias actuales (la UI debe impedirlo antes).
   */
  saveCheckoutProfile(
    businessId: string,
    perfil: TiendaCheckoutProfileInput,
  ): TiendaCheckoutPreferences {
    const current = this.getCheckoutPreferences(businessId);
    const existente = perfil.id
      ? current.perfiles.find((p) => p.id === perfil.id)
      : undefined;
    const etiqueta = perfil.etiqueta.trim() || "Mis datos";
    const actualizadoEn = new Date().toISOString();

    if (existente) {
      const perfiles = current.perfiles.map((p) =>
        p.id === existente.id
          ? {
              ...p,
              etiqueta,
              nombre: perfil.nombre,
              telefono: perfil.telefono,
              direccion: perfil.direccion,
              actualizadoEn,
            }
          : p,
      );
      return this.saveCheckoutPreferences(businessId, {
        perfiles,
        perfilActivoId: existente.id,
      });
    }

    if (current.perfiles.length >= MAX_CHECKOUT_PROFILES) return current;

    const nuevo: TiendaCheckoutProfile = {
      id: createCheckoutProfileId(),
      etiqueta,
      nombre: perfil.nombre,
      telefono: perfil.telefono,
      direccion: perfil.direccion,
      actualizadoEn,
    };
    return this.saveCheckoutPreferences(businessId, {
      perfiles: [...current.perfiles, nuevo],
      perfilActivoId: nuevo.id,
    });
  },

  deleteCheckoutProfile(
    businessId: string,
    profileId: string,
  ): TiendaCheckoutPreferences {
    const current = this.getCheckoutPreferences(businessId);
    const perfiles = current.perfiles.filter((p) => p.id !== profileId);
    const perfilActivoId =
      current.perfilActivoId === profileId ? null : current.perfilActivoId;
    return this.saveCheckoutPreferences(businessId, {
      perfiles,
      perfilActivoId,
    });
  },

  setActiveCheckoutProfile(
    businessId: string,
    profileId: string | null,
  ): TiendaCheckoutPreferences {
    const current = this.getCheckoutPreferences(businessId);
    if (profileId && !current.perfiles.some((p) => p.id === profileId)) {
      return current;
    }
    return this.saveCheckoutPreferences(businessId, {
      perfilActivoId: profileId,
    });
  },

  // ── Admin (requieren autenticación) ──────────────────────────────────────

  async saveConfig(config: Partial<TiendaConfig>): Promise<TiendaConfig> {
    if (shouldPreferLocalDemoOps()) {
      const current = txRead("tienda_config", DEFAULT_TIENDA_CONFIG);
      const updated = { ...current, ...config };
      txWrite("tienda_config", updated);
      return updated as TiendaConfig;
    }
    try {
      const { data } = await api.put(`${PUBLIC_BASE}/config`, config);
      return unwrapApiData<TiendaConfig>(data, config as TiendaConfig);
    } catch (err) {
      if (shouldFallbackToLocalStorage(err)) {
        const current = txRead("tienda_config", DEFAULT_TIENDA_CONFIG);
        const updated = { ...current, ...config };
        txWrite("tienda_config", updated);
        return updated as TiendaConfig;
      }
      throw err;
    }
  },

  async getPedidos(): Promise<Pedido[]> {
    if (shouldPreferLocalDemoOps()) {
      const raw = txRead("tienda_pedidos", []);
      return Array.isArray(raw) ? (raw as Pedido[]) : [];
    }
    try {
      const { data } = await api.get(`${PUBLIC_BASE}/pedidos`);
      return unwrapApiList<Pedido>(data, "pedidos");
    } catch (err) {
      if (shouldFallbackToLocalStorage(err)) {
        const raw = txRead("tienda_pedidos", []);
        return Array.isArray(raw) ? (raw as Pedido[]) : [];
      }
      throw err;
    }
  },

  async cambiarEstadoPedido(
    pedidoId: string,
    estado: EstadoPedido,
    notasInternas?: string,
    domiciliarioId?: string,
    domiciliarioNombre?: string,
  ): Promise<Pedido> {
    if (shouldPreferLocalDemoOps()) {
      const pedidos: Pedido[] = txRead("tienda_pedidos", []);
      const idx = pedidos.findIndex((p) => p.id === pedidoId);
      if (idx !== -1) {
        pedidos[idx] = {
          ...pedidos[idx],
          estado,
          ...(notasInternas !== undefined && { notasInternas }),
          ...(domiciliarioId !== undefined && { domiciliarioId }),
          ...(domiciliarioNombre !== undefined && { domiciliarioNombre }),
          fechaActualizacion: new Date().toISOString(),
        };
        txWrite("tienda_pedidos", pedidos);
        return pedidos[idx];
      }
    }
    try {
      const { data } = await api.patch(
        `${PUBLIC_BASE}/pedido/${pedidoId}/estado`,
        { estado, notasInternas, domiciliarioId, domiciliarioNombre },
      );
      return unwrapApiEntity<Pedido>(data, {} as Pedido);
    } catch (err) {
      if (shouldFallbackToLocalStorage(err)) {
        const pedidos: Pedido[] = txRead("tienda_pedidos", []);
        const idx = pedidos.findIndex((p) => p.id === pedidoId);
        if (idx !== -1) {
          pedidos[idx] = {
            ...pedidos[idx],
            estado,
            ...(notasInternas !== undefined && { notasInternas }),
            ...(domiciliarioId !== undefined && { domiciliarioId }),
            ...(domiciliarioNombre !== undefined && { domiciliarioNombre }),
            fechaActualizacion: new Date().toISOString(),
          };
          txWrite("tienda_pedidos", pedidos);
          return pedidos[idx];
        }
      }
      throw err;
    }
  },

  async cambiarMetodoPagoPedido(
    pedidoId: string,
    metodoPago: MetodoPagoTienda,
    metodoPagoLabel?: string,
  ): Promise<Pedido> {
    if (shouldPreferLocalDemoOps()) {
      const pedidos: Pedido[] = txRead("tienda_pedidos", []);
      const idx = pedidos.findIndex((p) => p.id === pedidoId);
      if (idx !== -1) {
        pedidos[idx] = {
          ...pedidos[idx],
          cliente: {
            ...pedidos[idx].cliente,
            metodoPago,
            ...(metodoPagoLabel !== undefined && { metodoPagoLabel }),
          },
          fechaActualizacion: new Date().toISOString(),
        };
        txWrite("tienda_pedidos", pedidos);
        return pedidos[idx];
      }
    }
    try {
      const { data } = await api.patch(
        `${PUBLIC_BASE}/pedido/${pedidoId}/metodo-pago`,
        { metodoPago, metodoPagoLabel },
      );
      return unwrapApiEntity<Pedido>(data, {} as Pedido);
    } catch (err) {
      if (shouldFallbackToLocalStorage(err)) {
        const pedidos: Pedido[] = txRead("tienda_pedidos", []);
        const idx = pedidos.findIndex((p) => p.id === pedidoId);
        if (idx !== -1) {
          pedidos[idx] = {
            ...pedidos[idx],
            cliente: {
              ...pedidos[idx].cliente,
              metodoPago,
              ...(metodoPagoLabel !== undefined && { metodoPagoLabel }),
            },
            fechaActualizacion: new Date().toISOString(),
          };
          txWrite("tienda_pedidos", pedidos);
          return pedidos[idx];
        }
      }
      throw err;
    }
  },

  async actualizarPedidoItem(
    pedidoId: string,
    itemIndex: number,
    item: PedidoItem,
  ): Promise<Pedido> {
    const { data } = await api.patch(
      `${PUBLIC_BASE}/pedido/${pedidoId}/items`,
      { itemIndex, item },
    );
    return unwrapApiEntity<Pedido>(data, {} as Pedido);
  },

  /** Reemplaza la lista completa de ítems (cantidad, agregar, quitar). */
  async actualizarPedidoItems(
    pedidoId: string,
    items: PedidoItem[],
  ): Promise<Pedido> {
    const { data } = await api.patch(
      `${PUBLIC_BASE}/pedido/${pedidoId}/items`,
      { items },
    );
    return unwrapApiEntity<Pedido>(data, {} as Pedido);
  },

  async actualizarPedidoCliente(
    pedidoId: string,
    cliente: Partial<{
      nombre: string;
      telefono: string;
      direccion?: string;
      cedula?: string;
      notas?: string;
    }>,
  ): Promise<Pedido> {
    const { data } = await api.patch(
      `${PUBLIC_BASE}/pedido/${pedidoId}/cliente`,
      { cliente },
    );
    return unwrapApiEntity<Pedido>(data, {} as Pedido);
  },

  async anularPedido(
    pedidoId: string,
    motivoAnulacion: string,
    anuladoPor: string,
  ): Promise<Pedido> {
    const { data } = await api.patch(
      `${PUBLIC_BASE}/pedido/${pedidoId}/anular`,
      { motivoAnulacion, anuladoPor },
    );
    return unwrapApiEntity<Pedido>(data, {} as Pedido);
  },

  async registrarDevolucionPedido(
    pedidoId: string,
    payload: {
      items: Array<{
        productoId: string;
        nombre: string;
        cantidad: number;
        precioUnitario: number;
      }>;
      montoReembolsado: number;
      metodoPago: string;
      motivoDevolucion: string;
      usuarioId: string;
      userName: string;
    },
  ): Promise<{ pedido: Pedido; devolucion: DevolucionPedido }> {
    const { data } = await api.patch(
      `${PUBLIC_BASE}/pedido/${pedidoId}/devolucion`,
      payload,
    );
    return unwrapApiData(data, {
      pedido: {} as Pedido,
      devolucion: {} as DevolucionPedido,
    });
  },
};

/**
 * Contrato público de UI: Caja monta el panel de auto-impresión de pedidos
 * desde el service de Tienda, sin importar componentes internos de la feature.
 */
export { PedidoAutoPrintSettings } from "../components/PedidoAutoPrintSettings";
